import React, { useEffect, useState } from 'react';
import { useAuth } from '../context/AuthContext';
import api from '../utils/api';
import { toast } from 'sonner';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Badge } from '@/components/ui/badge';
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from '@/components/ui/select';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger,
} from '@/components/ui/dialog';
import { Plus, Mail, Phone, User, Users, Tag, X, Settings, Trash2, Search } from 'lucide-react';

const LABEL_COLORS = [
  '#4A6B5D', '#C28E72', '#6366F1', '#EC4899', '#F59E0B',
  '#10B981', '#EF4444', '#3B82F6', '#8B5CF6', '#14B8A6',
];

export const Clients = () => {
  const { isAdmin } = useAuth();
  const [clients, setClients] = useState([]);
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  const [filterLabel, setFilterLabel] = useState('');

  // Etichette
  const [etichette, setEtichette] = useState([]);
  const [labelDialogOpen, setLabelDialogOpen] = useState(false);
  const [newLabelName, setNewLabelName] = useState('');
  const [newLabelColor, setNewLabelColor] = useState(LABEL_COLORS[0]);

  const [formData, setFormData] = useState({
    nome: '', cognome: '', telefono: '', email: '', note: '', stato: 'lead', dipendente_assegnato_id: '', etichette: [],
  });

  useEffect(() => { fetchClients(); fetchEtichette(); if (isAdmin) fetchUsers(); }, []);

  const fetchClients = async () => {
    try { const r = await api.get('/clients'); setClients(r.data); } catch { toast.error('Errore caricamento clienti'); } finally { setLoading(false); }
  };
  const fetchUsers = async () => {
    try { const r = await api.get('/users'); setUsers(r.data.filter(u => u.ruolo === 'dipendente')); } catch {}
  };
  const fetchEtichette = async () => {
    try { const r = await api.get('/etichette'); setEtichette(r.data); } catch {}
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    try { await api.post('/clients', formData); toast.success('Cliente creato!'); setDialogOpen(false); resetForm(); fetchClients(); } catch { toast.error('Errore creazione cliente'); }
  };
  const resetForm = () => setFormData({ nome: '', cognome: '', telefono: '', email: '', note: '', stato: 'lead', dipendente_assegnato_id: '', etichette: [] });

  const handleCreateLabel = async () => {
    if (!newLabelName.trim()) return;
    try {
      await api.post('/etichette', { nome: newLabelName.trim(), colore: newLabelColor });
      toast.success('Etichetta creata!');
      setNewLabelName(''); setNewLabelColor(LABEL_COLORS[0]);
      fetchEtichette();
    } catch (err) { toast.error(err.response?.data?.detail || 'Errore'); }
  };

  const handleDeleteLabel = async (id) => {
    try { await api.delete(`/etichette/${id}`); toast.success('Etichetta eliminata'); fetchEtichette(); fetchClients(); } catch { toast.error('Errore eliminazione'); }
  };

  const handleAssignLabel = async (clientId, labelName) => {
    try { await api.post(`/etichette/client/${clientId}/assign`, { etichetta: labelName }); fetchClients(); } catch { toast.error('Errore assegnazione'); }
  };
  const handleRemoveLabel = async (clientId, labelName) => {
    try { await api.post(`/etichette/client/${clientId}/remove`, { etichetta: labelName }); fetchClients(); } catch { toast.error('Errore rimozione'); }
  };

  const getLabelColor = (name) => etichette.find(e => e.nome === name)?.colore || '#78716C';

  const statusColors = {
    lead: 'bg-blue-50 text-blue-600 border-blue-200',
    attivo: 'bg-green-50 text-green-600 border-green-200',
    inattivo: 'bg-slate-50 text-slate-600 border-slate-200',
    archiviato: 'bg-red-50 text-red-600 border-red-200'
  };

  const filteredClients = clients.filter(c => {
    const q = searchQuery.toLowerCase();
    const matchesSearch = !q || `${c.nome} ${c.cognome} ${c.email || ''} ${c.telefono || ''}`.toLowerCase().includes(q);
    const matchesLabel = !filterLabel || (c.etichette || []).includes(filterLabel);
    return matchesSearch && matchesLabel;
  });

  if (loading) return (
    <div className="flex items-center justify-center h-screen">
      <div className="inline-block animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600"></div>
    </div>
  );

  return (
    <div className="p-3 sm:p-6 md:p-8 lg:p-10" data-testid="clients-page">
      {/* Header */}
      <div className="flex items-center justify-between mb-6">
        <div>
          <h1 className="text-3xl sm:text-4xl font-bold text-slate-900 tracking-tight mb-1">Clienti</h1>
          <p className="text-sm text-slate-500">{filteredClients.length} clienti{filterLabel ? ` con etichetta "${filterLabel}"` : ''}</p>
        </div>
        <div className="flex items-center gap-2">
          <Button variant="outline" size="sm" onClick={() => setLabelDialogOpen(true)} data-testid="manage-labels-btn">
            <Tag size={15} className="mr-1.5" /> Etichette
          </Button>
          <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
            <DialogTrigger asChild>
              <Button data-testid="create-client-button" className="bg-blue-600 hover:bg-blue-700" size="sm">
                <Plus size={16} className="mr-1.5" /> Nuovo Cliente
              </Button>
            </DialogTrigger>
            <DialogContent className="max-w-lg">
              <DialogHeader><DialogTitle>Aggiungi Nuovo Cliente</DialogTitle></DialogHeader>
              <form onSubmit={handleSubmit} className="space-y-4">
                <div className="grid grid-cols-2 gap-4">
                  <div><Label htmlFor="nome">Nome</Label><Input id="nome" value={formData.nome} onChange={(e) => setFormData({ ...formData, nome: e.target.value })} required data-testid="client-name-input" /></div>
                  <div><Label htmlFor="cognome">Cognome</Label><Input id="cognome" value={formData.cognome} onChange={(e) => setFormData({ ...formData, cognome: e.target.value })} required data-testid="client-surname-input" /></div>
                </div>
                <div><Label htmlFor="email">Email</Label><Input id="email" type="email" value={formData.email} onChange={(e) => setFormData({ ...formData, email: e.target.value })} data-testid="client-email-input" /></div>
                <div><Label htmlFor="telefono">Telefono</Label><Input id="telefono" value={formData.telefono} onChange={(e) => setFormData({ ...formData, telefono: e.target.value })} data-testid="client-phone-input" /></div>
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <Label>Stato</Label>
                    <Select value={formData.stato} onValueChange={(v) => setFormData({ ...formData, stato: v })}>
                      <SelectTrigger data-testid="client-status-select"><SelectValue /></SelectTrigger>
                      <SelectContent>
                        <SelectItem value="lead">Lead</SelectItem><SelectItem value="attivo">Attivo</SelectItem>
                        <SelectItem value="inattivo">Inattivo</SelectItem><SelectItem value="archiviato">Archiviato</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                  {isAdmin && (
                    <div>
                      <Label>Assegna a</Label>
                      <Select value={formData.dipendente_assegnato_id} onValueChange={(v) => setFormData({ ...formData, dipendente_assegnato_id: v })}>
                        <SelectTrigger data-testid="client-assignee-select"><SelectValue placeholder="Seleziona..." /></SelectTrigger>
                        <SelectContent>{users.map(u => <SelectItem key={u.id} value={u.id}>{u.nome} {u.cognome}</SelectItem>)}</SelectContent>
                      </Select>
                    </div>
                  )}
                </div>
                {etichette.length > 0 && (
                  <div>
                    <Label>Etichette</Label>
                    <div className="flex flex-wrap gap-1.5 mt-1.5">
                      {etichette.map(et => {
                        const sel = formData.etichette.includes(et.nome);
                        return (
                          <button key={et.id} type="button"
                            className={`px-2.5 py-1 rounded-full text-xs font-medium border transition-all ${sel ? 'ring-2 ring-offset-1' : 'opacity-60 hover:opacity-100'}`}
                            style={{ backgroundColor: sel ? et.colore + '20' : 'transparent', borderColor: et.colore, color: et.colore, ...(sel ? { ringColor: et.colore } : {}) }}
                            onClick={() => setFormData({ ...formData, etichette: sel ? formData.etichette.filter(e => e !== et.nome) : [...formData.etichette, et.nome] })}
                            data-testid={`label-toggle-${et.nome}`}>
                            {et.nome}
                          </button>
                        );
                      })}
                    </div>
                  </div>
                )}
                <div><Label htmlFor="note">Note</Label><Textarea id="note" value={formData.note} onChange={(e) => setFormData({ ...formData, note: e.target.value })} rows={3} data-testid="client-notes-input" /></div>
                <Button type="submit" className="w-full" data-testid="client-submit-button">Aggiungi Cliente</Button>
              </form>
            </DialogContent>
          </Dialog>
        </div>
      </div>

      {/* Search + Label Filters */}
      <div className="flex flex-wrap items-center gap-3 mb-6">
        <div className="relative flex-1 min-w-[200px] max-w-md">
          <Search size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
          <Input placeholder="Cerca per nome, email, telefono..." value={searchQuery} onChange={e => setSearchQuery(e.target.value)} className="pl-9 h-9 text-sm" data-testid="client-search-input" />
        </div>
        {etichette.length > 0 && (
          <div className="flex flex-wrap items-center gap-1.5">
            <button onClick={() => setFilterLabel('')}
              className={`px-2.5 py-1 rounded-full text-xs font-medium border transition-all ${!filterLabel ? 'bg-slate-900 text-white border-slate-900' : 'bg-white text-slate-600 border-slate-200 hover:border-slate-400'}`}
              data-testid="filter-all">Tutti</button>
            {etichette.map(et => (
              <button key={et.id} onClick={() => setFilterLabel(filterLabel === et.nome ? '' : et.nome)}
                className={`px-2.5 py-1 rounded-full text-xs font-medium border transition-all ${filterLabel === et.nome ? 'ring-2 ring-offset-1' : 'opacity-70 hover:opacity-100'}`}
                style={{ backgroundColor: filterLabel === et.nome ? et.colore + '20' : 'transparent', borderColor: et.colore, color: et.colore }}
                data-testid={`filter-label-${et.nome}`}>
                {et.nome}
              </button>
            ))}
          </div>
        )}
      </div>

      {/* Client Cards */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-5">
        {filteredClients.map(client => (
          <div key={client.id} data-testid={`client-card-${client.id}`}
            className="bg-white rounded-xl border border-slate-200 p-5 hover:shadow-md transition-shadow">
            <div className="flex items-start justify-between mb-3">
              <div className="flex items-center gap-3">
                <div className="w-11 h-11 rounded-full bg-blue-100 flex items-center justify-center flex-shrink-0">
                  <User size={20} className="text-blue-600" />
                </div>
                <div>
                  <h3 className="font-semibold text-slate-900 text-sm">{client.nome} {client.cognome}</h3>
                  <span className={`inline-block px-2 py-0.5 rounded text-[11px] font-medium mt-0.5 border ${statusColors[client.stato]}`}>{client.stato}</span>
                </div>
              </div>
            </div>

            {/* Labels */}
            <div className="flex flex-wrap gap-1 mb-3 min-h-[24px]">
              {(client.etichette || []).map(label => (
                <span key={label} className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[11px] font-medium border"
                  style={{ backgroundColor: getLabelColor(label) + '15', borderColor: getLabelColor(label) + '40', color: getLabelColor(label) }}
                  data-testid={`client-label-${client.id}-${label}`}>
                  {label}
                  <button onClick={() => handleRemoveLabel(client.id, label)} className="hover:opacity-70" data-testid={`remove-label-${client.id}-${label}`}>
                    <X size={10} />
                  </button>
                </span>
              ))}
              {/* Quick add label dropdown */}
              {etichette.filter(e => !(client.etichette || []).includes(e.nome)).length > 0 && (
                <div className="relative group">
                  <button className="h-5 w-5 rounded-full border border-dashed border-slate-300 flex items-center justify-center hover:border-slate-500 transition-colors" data-testid={`add-label-trigger-${client.id}`}>
                    <Plus size={10} className="text-slate-400" />
                  </button>
                  <div className="absolute left-0 top-full mt-1 bg-white rounded-lg shadow-lg border border-slate-200 py-1 z-20 hidden group-hover:block min-w-[140px]">
                    {etichette.filter(e => !(client.etichette || []).includes(e.nome)).map(et => (
                      <button key={et.id} onClick={() => handleAssignLabel(client.id, et.nome)}
                        className="w-full text-left px-3 py-1.5 text-xs hover:bg-slate-50 flex items-center gap-2"
                        data-testid={`assign-label-${client.id}-${et.nome}`}>
                        <span className="w-2.5 h-2.5 rounded-full flex-shrink-0" style={{ backgroundColor: et.colore }} />
                        {et.nome}
                      </button>
                    ))}
                  </div>
                </div>
              )}
            </div>

            <div className="space-y-1.5">
              {client.email && <div className="flex items-center gap-2 text-xs text-slate-500"><Mail size={13} /><span className="truncate">{client.email}</span></div>}
              {client.telefono && <div className="flex items-center gap-2 text-xs text-slate-500"><Phone size={13} /><span>{client.telefono}</span></div>}
              {client.dipendente_assegnato_nome && <div className="flex items-center gap-2 text-xs text-slate-500"><User size={13} /><span>Assegnato a: {client.dipendente_assegnato_nome}</span></div>}
            </div>

            {client.note && <div className="mt-3 pt-3 border-t border-slate-100"><p className="text-xs text-slate-500 line-clamp-2">{client.note}</p></div>}
          </div>
        ))}
      </div>

      {filteredClients.length === 0 && (
        <div className="text-center py-12">
          <div className="inline-block p-6 rounded-full bg-slate-100 mb-4"><Users size={48} className="text-slate-400" /></div>
          <h3 className="text-xl font-semibold text-slate-900 mb-2">{searchQuery || filterLabel ? 'Nessun risultato' : 'Nessun cliente'}</h3>
          <p className="text-slate-600">{searchQuery || filterLabel ? 'Prova a cambiare i filtri' : 'Inizia aggiungendo il tuo primo cliente'}</p>
        </div>
      )}

      {/* Manage Labels Dialog */}
      <Dialog open={labelDialogOpen} onOpenChange={setLabelDialogOpen}>
        <DialogContent className="max-w-md">
          <DialogHeader><DialogTitle className="flex items-center gap-2"><Tag size={18} /> Gestione Etichette</DialogTitle></DialogHeader>
          <div className="space-y-4">
            {/* Create new label */}
            <div className="flex items-end gap-2">
              <div className="flex-1">
                <Label className="text-xs">Nuova etichetta</Label>
                <Input value={newLabelName} onChange={e => setNewLabelName(e.target.value)} placeholder="Nome etichetta..." className="h-9 text-sm mt-1"
                  onKeyDown={e => e.key === 'Enter' && handleCreateLabel()} data-testid="new-label-name-input" />
              </div>
              <div>
                <Label className="text-xs">Colore</Label>
                <div className="flex gap-1 mt-1">
                  {LABEL_COLORS.map(c => (
                    <button key={c} onClick={() => setNewLabelColor(c)}
                      className={`w-6 h-6 rounded-full border-2 transition-all ${newLabelColor === c ? 'border-slate-900 scale-110' : 'border-transparent hover:scale-105'}`}
                      style={{ backgroundColor: c }} data-testid={`label-color-${c}`} />
                  ))}
                </div>
              </div>
              <Button size="sm" onClick={handleCreateLabel} className="h-9" data-testid="create-label-btn">
                <Plus size={14} />
              </Button>
            </div>

            {/* Existing labels */}
            {etichette.length === 0 ? (
              <p className="text-sm text-slate-400 text-center py-4">Nessuna etichetta creata</p>
            ) : (
              <div className="space-y-1.5 max-h-[300px] overflow-y-auto">
                {etichette.map(et => (
                  <div key={et.id} className="flex items-center justify-between px-3 py-2 rounded-lg hover:bg-slate-50 group" data-testid={`label-item-${et.id}`}>
                    <div className="flex items-center gap-2.5">
                      <span className="w-3.5 h-3.5 rounded-full flex-shrink-0" style={{ backgroundColor: et.colore }} />
                      <span className="text-sm font-medium text-slate-700">{et.nome}</span>
                    </div>
                    <button onClick={() => handleDeleteLabel(et.id)} className="opacity-0 group-hover:opacity-100 text-slate-400 hover:text-red-500 transition-all p-1" data-testid={`delete-label-${et.id}`}>
                      <Trash2 size={14} />
                    </button>
                  </div>
                ))}
              </div>
            )}
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
};
